import { apiClient, apiClientPaginated, apiBlob, ApiError } from '@/lib/api-client';
import type {
  CreateRfqPayload,
  Rfq,
  RfqListParams,
  RfqListResponse,
  RfqStats,
  SendRfqPayload,
  SendRfqResult,
  SubmitQuotePayload,
  AwardRfqPayload,
  UpdateRfqPayload,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_STATS: RfqStats = { total: 0, by_status: { DRAFT: 0, SENT: 0, CLOSED: 0, CANCELLED: 0 } };

export const rfqsApi = {
  // Not wrapped in withFallback: a 404/501 must reach the list page as an error,
  // not masquerade as "no RFQs yet" (same reasoning as purchaseOrdersApi.list).
  list: (params: RfqListParams) =>
    apiClientPaginated<RfqListResponse['data']>(
      `/rfqs${buildQuery({
        page: params.page,
        limit: params.limit,
        location_id: params.location_id,
        status: params.status || undefined,
        search: params.search,
        start_date: params.start_date,
        end_date: params.end_date,
      })}`,
    ) as Promise<RfqListResponse>,

  stats: (params: { location_id?: string }) =>
    withFallback(() => apiClient<RfqStats>(`/rfqs/stats${buildQuery({ location_id: params.location_id })}`), EMPTY_STATS),

  getById: (id: string) => withFallback(() => apiClient<Rfq>(`/rfqs/${id}`), null),

  create: (payload: CreateRfqPayload) => apiClient<Rfq>('/rfqs', { method: 'POST', body: payload }),

  update: (id: string, payload: UpdateRfqPayload) => apiClient<Rfq>(`/rfqs/${id}`, { method: 'PUT', body: payload }),

  send: (id: string, payload: SendRfqPayload) => apiClient<SendRfqResult>(`/rfqs/${id}/send`, { method: 'POST', body: payload }),

  submitQuote: (id: string, rfqSupplierId: string, payload: SubmitQuotePayload) =>
    apiClient<Rfq>(`/rfqs/${id}/suppliers/${rfqSupplierId}/quote`, { method: 'PUT', body: payload }),

  close: (id: string) => apiClient<Rfq>(`/rfqs/${id}/close`, { method: 'POST' }),

  award: (id: string, payload: AwardRfqPayload) => apiClient<Rfq>(`/rfqs/${id}/award`, { method: 'POST', body: payload }),

  cancel: (id: string, reason: string) => apiClient<Rfq>(`/rfqs/${id}/cancel`, { method: 'POST', body: { reason } }),

  downloadPdf: (id: string) => apiBlob(`/rfqs/${id}/pdf`),
};
